/** Product Page Promotion Widget — store-wide style (campaigns only set title/subtitle). */

export type PromoFontWeight = 'regular' | 'medium' | 'semibold' | 'bold';

export interface PromoDesign {
  titleFontWeight: PromoFontWeight;
  subtitleFontWeight: PromoFontWeight;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  mutedColor: string;
  borderColor: string;
  giftCardBackground: string;
  giftCardBorder: string;
  comparePriceColor: string;
  borderRadius: number;
  fontFamily: string;
  autoFitTheme: boolean;
  themeSchemeId: string | null;
}

export const DEFAULT_PROMO_DESIGN: PromoDesign = {
  titleFontWeight: 'bold',
  subtitleFontWeight: 'regular',
  accentColor: '#1A1A1A',
  backgroundColor: '#F7F3EA',
  textColor: '#2B1A16',
  mutedColor: '#6B6B6B',
  borderColor: '#E4DDD0',
  giftCardBackground: '#FFFFFF',
  giftCardBorder: '#E8E2D6',
  comparePriceColor: '#9A9A9A',
  borderRadius: 16,
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  autoFitTheme: true,
  themeSchemeId: null,
};

export const DEFAULT_PROMO_TITLE = 'EXCLUSIVE GIFTS!';
export const DEFAULT_PROMO_SUBTITLE_SPEND =
  'Spend {{trigger_value}} to receive gifts';
export const DEFAULT_PROMO_SUBTITLE_QTY =
  'Buy {{trigger_value}} items to receive gifts';
export const DEFAULT_PROMO_SUBTITLE_BXGY =
  'Buy {{trigger_value}} items to receive gifts';

export function promoFontWeightCss(w: PromoFontWeight | string | undefined): number {
  if (w === 'medium') return 500;
  if (w === 'semibold') return 600;
  if (w === 'bold') return 700;
  return 400;
}

/** Accepts {{trigger_value}} or {trigger_value}. */
export function interpolatePromoCopy(
  template: string,
  triggerValue: string,
): string {
  return String(template || '')
    .replace(/\{\{\s*trigger_value\s*\}\}/gi, triggerValue)
    .replace(/\{\s*trigger_value\s*\}/gi, triggerValue);
}

export interface PromoPreviewGift {
  id: string;
  title: string;
  imageUrl?: string | null;
  /** Compare-at / original price label */
  priceLabel?: string | null;
  /** What the shopper pays after the campaign discount */
  offeredLabel?: string | null;
}

export function parseMoneyAmount(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const s = String(raw).replace(/[^0-9.]/g, '');
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/** Final price after campaign discount (0 = free). */
export function offeredAmount(
  original: number,
  discountType?: string | null,
  discountValue?: string | number | null,
): number {
  const t = String(discountType || 'percentage').toLowerCase();
  const n = Number(discountValue);
  if (t === 'free') return 0;
  if (t === 'percentage' || t === 'percent') {
    if (!Number.isFinite(n) || n >= 100) return 0;
    return Math.max(0, original * (1 - n / 100));
  }
  if (t === 'fixed' || t === 'fixed_amount' || t === 'fixed_price') {
    if (!Number.isFinite(n) || n <= 0) return original;
    return Math.max(0, original - n);
  }
  return original;
}

export function uniquePromoGifts(gifts: PromoPreviewGift[]): PromoPreviewGift[] {
  const seen = new Set<string>();
  const out: PromoPreviewGift[] = [];
  for (const g of gifts || []) {
    const key =
      String(g.id || '')
        .replace(/\D/g, '')
        .trim() || String(g.title || '').toLowerCase().trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(g);
  }
  return out;
}

export { toColorInputValue } from './popupDefaults';
